import {
  Handshake,
  Storefront,
  Wallet,
  Receipt,
  Package,
  SlidersHorizontal,
  Gift,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';

import { useGetPendingDeliveryCountQuery } from '@entities/admin';
import { useGetMeQuery } from '@entities/user';
import { haptics } from '@shared/lib/telegram';
import styles from './BottomNavBar.module.css';

const TABS = [
  { to: '/', label: 'Store', icon: Storefront },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: '/orders', label: 'Orders', icon: Receipt },
  { to: '/invite', label: 'Invite', icon: Gift },
] as const;

/** Admin-only, in the order they are used: add stock first, then run the shop. */
const ADMIN_TABS = [
  { to: '/products', label: 'Products', icon: Package },
  { to: '/panel', label: 'Panel', icon: SlidersHorizontal },
] as const;

export function BottomNavBar() {
  const { pathname } = useLocation();
  const { data: user } = useGetMeQuery();
  const { data: pendingDelivery } = useGetPendingDeliveryCountQuery(undefined, {
    skip: !user?.isAdmin,
    pollingInterval: 15_000,
    refetchOnFocus: true,
  });
  const pendingCount = pendingDelivery?.count ?? 0;

  // Private capabilities only appear after the server grants them. Every route
  // still performs its own authorization; this is presentation, not security.
  const resellerTabs = user?.isReseller
    ? [{ to: '/reseller', label: 'Reseller', icon: Handshake } as const]
    : [];
  const tabs = user?.isAdmin
    ? [...TABS, ...resellerTabs, ...ADMIN_TABS]
    : [...TABS, ...resellerTabs];

  return (
    <nav className={styles.nav} data-tabs={tabs.length}>
      {tabs.map((tab) => {
        // A detail route under /panel should keep the Panel tab lit.
        const active = tab.to === '/' ? pathname === '/' : pathname.startsWith(tab.to);
        const Icon = tab.icon;

        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={styles.tab}
            onClick={() => {
              if (!active) haptics.select();
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
          >
            {/* The pill slides between tabs instead of fading in place. */}
            {active ? (
              <motion.span
                layoutId="nav-pill"
                className={styles.pill}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            ) : null}
            <span className={active ? styles.contentActive : styles.content}>
              <span className={styles.iconWrap}>
                <Icon size={22} weight={active ? 'fill' : 'regular'} />
                {tab.to === '/products' && pendingCount > 0 ? (
                  <span
                    className={styles.badge}
                    aria-label={`${pendingCount} pending in-house ${pendingCount === 1 ? 'order' : 'orders'}`}
                  >
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                ) : null}
              </span>
              <span className={styles.label}>{tab.label}</span>
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
}
